import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  loadEntries,
  createEntry,
  updateEntry,
  deleteEntry
} from '../api/entriesApi';
import { currentUser } from '../utils/runtime';
import { useGamification } from './GamificationContext';
import { useOptionalAppSession } from './AppSessionContext';
import { emitDrinkDiscoveryChanges } from '../utils/drinkDiscovery';

const QUICK_POSITION_OPTIONS = {
  enableHighAccuracy: false,
  timeout: 2500,
  maximumAge: 60000
};

const PRECISE_POSITION_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 10000,
  maximumAge: 0
};

function requestPosition(options) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation wird vom Browser nicht unterstützt.'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      pos => {
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy ?? null
        });
      },
      error => {
        reject(error);
      },
      options
    );
  });
}

async function resolveBestEffortPosition() {
  try {
    return await requestPosition(QUICK_POSITION_OPTIONS);
  } catch (quickError) {
    try {
      return await requestPosition(PRECISE_POSITION_OPTIONS);
    } catch (preciseError) {
      console.warn('[Pfandpirat] Standort konnte nicht rechtzeitig ermittelt werden.', {
        quickError,
        preciseError
      });
      return null;
    }
  }
}

function normalizeBootstrapEntries(session) {
  const items = session?.entries?.items;
  return Array.isArray(items) ? items : null;
}

export function usePfandEntries() {
  const sessionContext = useOptionalAppSession();
  const bootstrapEntries = normalizeBootstrapEntries(sessionContext?.session);
  const hasBootstrapEntries = Array.isArray(bootstrapEntries);
  const { applyGamificationPayload } = useGamification() || {};
  const [entries, setEntriesState] = useState(() => (hasBootstrapEntries ? bootstrapEntries : []));
  const [loading, setLoading] = useState(!hasBootstrapEntries);
  const [error, setError] = useState(null);

  const user = useMemo(() => {
    const sessionUser = sessionContext?.session?.user;
    if (sessionUser?.id) {
      return {
        id: Number(sessionUser.id || 0),
        name: sessionUser.display_name || sessionUser.name || currentUser.name,
      };
    }
    return currentUser;
  }, [sessionContext?.session?.user]);

  const syncEntriesToSession = useCallback((nextEntries) => {
    if (!sessionContext?.mergeSessionPatch || !Array.isArray(nextEntries)) {
      return;
    }

    sessionContext.mergeSessionPatch({
      entries: {
        items: nextEntries,
        loaded: nextEntries.length,
        limit: sessionContext.session?.entries?.limit || nextEntries.length,
      },
    });
  }, [sessionContext]);

  const setEntries = useCallback((updater) => {
    setEntriesState((previous) => {
      const nextEntries = typeof updater === 'function' ? updater(previous) : updater;
      if (Array.isArray(nextEntries)) {
        syncEntriesToSession(nextEntries);
      }
      return nextEntries;
    });
  }, [syncEntriesToSession]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const nextEntries = await loadEntries();
      setEntriesState(nextEntries);
      syncEntriesToSession(nextEntries);
      return nextEntries;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [syncEntriesToSession]);

  useEffect(() => {
    if (hasBootstrapEntries) {
      setEntriesState(bootstrapEntries);
      setLoading(false);
      return;
    }

    load().catch(() => {});
  }, [hasBootstrapEntries, bootstrapEntries, load]);

  const add = useCallback(
    (amount, meta = {}, userIdParam = null) => {
      const currentUserId = userIdParam ?? user?.id ?? null;

      const optimisticId = `tmp-${Date.now()}`;
      const optimisticEntry = {
        id: optimisticId,
        amount,
        latitude: null,
        longitude: null,
        user_id: currentUserId,
        userId: currentUserId,
        barcode: meta.barcode ?? null,
        product_name: meta.product_name ?? null,
        volume: meta.volume ?? null,
        description: meta.description ?? null,
        pfand_info: meta.pfand_info ?? null,
        quantity: meta.quantity ?? 1,
        package_type: meta.package_type ?? 'single',
        package_label: meta.package_label ?? '',
        package_units: meta.package_units ?? 1,
        unit_amount: meta.unit_amount ?? amount,
        package_deposit: meta.package_deposit ?? amount,
        activity_source: meta.activity_source ?? null,
        source_id: meta.source_id ?? null,
        internal_url: meta.internal_url ?? null,
        external_url: meta.external_url ?? null,
        status: 'open',
        timestamp: new Date().toISOString().slice(0, 19).replace('T', ' ')
      };

      setEntries(prev => [optimisticEntry, ...prev]);

      const submit = async (latitude, longitude) => {
        const body = {
          amount,
          latitude,
          longitude,
          user_id: currentUserId,
          barcode: meta.barcode ?? null,
          product_name: meta.product_name ?? null,
          volume: meta.volume ?? null,
          description: meta.description ?? null,
          pfand_info: meta.pfand_info ?? null,
          quantity: meta.quantity ?? 1,
          package_type: meta.package_type ?? 'single',
          package_label: meta.package_label ?? '',
          package_units: meta.package_units ?? 1,
          unit_amount: meta.unit_amount ?? amount,
          package_deposit: meta.package_deposit ?? amount,
          activity_source: meta.activity_source ?? null,
          source_id: meta.source_id ?? null,
          internal_url: meta.internal_url ?? null,
          external_url: meta.external_url ?? null,
          status: 'open'
        };

        try {
          const newEntry = await createEntry(body);
          const hasCoordinates = latitude !== null && longitude !== null;

          setEntries(prev => [
            {
              ...newEntry,
              latitude: hasCoordinates ? latitude : newEntry.latitude ?? null,
              longitude: hasCoordinates ? longitude : newEntry.longitude ?? null
            },
            ...prev.filter(e => e.id !== optimisticId)
          ]);

          const expeditionBonus = newEntry?.expedition_bonus || null;
          const scannerEnergyAdded = Number(expeditionBonus?.scanner_energy_added || 0);

          if (scannerEnergyAdded > 0 && typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('pfandpirat:expedition-pfand-charge', {
              detail: expeditionBonus
            }));
          }

          if (typeof applyGamificationPayload === 'function') {
            const awarded = Number(newEntry?.awarded || 0);
            if (awarded !== 0) {
              applyGamificationPayload(newEntry, {
                emitXpToast: true,
                event: newEntry?.event || 'add_pfand',
                awarded,
                toastLabel: newEntry?.xp_toast?.label || ''
              });
            }

            if (expeditionBonus?.triggered) {
              applyGamificationPayload(expeditionBonus, {
                emitXpToast: true,
                event: expeditionBonus.event || 'expedition_pfand_bonus',
                awarded: Number(expeditionBonus.awarded || 0),
                toastLabel: expeditionBonus?.xp_toast?.label || 'Pfand-Beifang entdeckt'
              });
            } else if (Array.isArray(expeditionBonus?.events) && expeditionBonus.events.length) {
              applyGamificationPayload(expeditionBonus, {
                emitXpToast: false
              });
            }
          }

          if (newEntry?.session_patch && sessionContext?.mergeSessionPatch) {
            sessionContext.mergeSessionPatch(newEntry.session_patch);
          }

          if (newEntry?.drink_discovery) {
            emitDrinkDiscoveryChanges(newEntry.drink_discovery);
          }

          setTimeout(() => {
            load().catch(() => {});
          }, 1200);

          return newEntry;
        } catch (err) {
          setEntries(prev => prev.filter(e => e.id !== optimisticId));
          throw err;
        }
      };

      const startBackgroundSync = async () => {
          const position = await resolveBestEffortPosition();

          if (position) {
            optimisticEntry.latitude = position.latitude;
            optimisticEntry.longitude = position.longitude;
            setEntries(prev => prev.map(entry => (
              entry.id === optimisticId
                ? {
                    ...entry,
                    latitude: position.latitude,
                    longitude: position.longitude
                  }
                : entry
            )));

            return submit(position.latitude, position.longitude);
          }

          return submit(null, null);
      };

      const backgroundPromise = startBackgroundSync().catch((err) => {
          console.error('[Pfandpirat] Pfandeintrag konnte nicht gespeichert werden.', err);
          setEntries(prev => prev.filter(e => e.id !== optimisticId));
          throw err;
      });

      if (meta.awaitServer === true) return backgroundPromise;
      void backgroundPromise.catch(() => {});
      return Promise.resolve(optimisticEntry);
    },
    [applyGamificationPayload, load, sessionContext, setEntries, user]
  );

  const update = useCallback(async (id, fields) => {
    const response = await updateEntry(id, fields);
    setEntries(prev => prev.map(e => (e.id === id ? { ...e, ...fields } : e)));
    if (response?.session_patch && sessionContext?.mergeSessionPatch) {
      sessionContext.mergeSessionPatch(response.session_patch);
    }
    if (response?.drink_discovery) {
      emitDrinkDiscoveryChanges(response.drink_discovery);
    }
    return response;
  }, [sessionContext, setEntries]);

  const remove = useCallback(async id => {
    const response = await deleteEntry(id);
    setEntries(prev => prev.filter(e => e.id !== id));
    if (response?.session_patch && sessionContext?.mergeSessionPatch) {
      sessionContext.mergeSessionPatch(response.session_patch);
    }
    if (response?.drink_discovery) {
      emitDrinkDiscoveryChanges(response.drink_discovery);
    }
    return response;
  }, [sessionContext, setEntries]);

  const applyReceiptRedemptionResult = useCallback((result) => {
    const patch = result?.session_patch;
    const patchedEntries = patch?.entries?.items;
    if (Array.isArray(patchedEntries)) {
      setEntriesState(patchedEntries);
    }
    if (patch && sessionContext?.mergeSessionPatch) {
      sessionContext.mergeSessionPatch(patch);
    }
    return Array.isArray(patchedEntries);
  }, [sessionContext]);

  return {
    entries,
    setEntries,
    add,
    update,
    remove,
    applyReceiptRedemptionResult,
    load,
    user,
    loading,
    error
  };
}

export default usePfandEntries;
